import React, { useEffect, useState } from "react";
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";

const CATEGORIES = ["All", "Wedding", "Birthday", "Corporate Events", "Smoke Effects", "Fireworks", "Stage Effects", "Event Setup"];

const Gallery = () => {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("All");
    const [lightbox, setLightbox] = useState(null); // index

    useEffect(() => {
        const cat = filter === "All" ? "" : `?category=${encodeURIComponent(filter)}`;
        api.get(`/gallery${cat}`).then(r => setItems(r.data));
    }, [filter]);

    useEffect(() => {
        const onKey = (e) => {
            if (lightbox === null) return;
            if (e.key === "Escape") setLightbox(null);
            if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % items.length);
            if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + items.length) % items.length);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox, items.length]);

    return (
        <div data-testid="gallery-page" className="pt-32 pb-24 bg-bg-base">
            <section className="px-6 md:px-12 mb-12">
                <div className="max-w-7xl mx-auto text-center">
                    <div className="text-xs uppercase tracking-[0.4em] text-gold mb-5">Our Work</div>
                    <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white leading-tight">
                        Moments we've <span className="italic gold-text-gradient">created</span>
                    </h1>
                </div>
            </section>

            {/* Filters */}
            <section className="px-6 md:px-12 mb-12">
                <div className="max-w-7xl mx-auto flex flex-wrap gap-2 justify-center">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c}
                            data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => setFilter(c)}
                            className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 border ${
                                filter === c
                                    ? "bg-gold text-black border-gold"
                                    : "border-white/15 text-white/75 hover:border-gold hover:text-gold"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </section>

            {/* Masonry */}
            <section className="px-6 md:px-12">
                <div className="max-w-7xl mx-auto">
                    {items.length === 0 ? (
                        <div className="text-center text-white/60 py-20">No images in this category yet.</div>
                    ) : (
                        <div className="masonry">
                            {items.map((it, idx) => (
                                <button
                                    key={it.id}
                                    data-testid={`gallery-item-${it.id}`}
                                    onClick={() => setLightbox(idx)}
                                    className="relative w-full group overflow-hidden rounded-lg"
                                >
                                    <img src={it.image_url} alt={it.title} className="w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
                                        <ZoomIn className="text-gold" size={28} />
                                    </div>
                                    {it.title && (
                                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent text-left">
                                            <div className="text-xs text-gold uppercase tracking-wider">{it.category}</div>
                                            <div className="text-sm text-white font-medium line-clamp-1">{it.title}</div>
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Lightbox */}
            {lightbox !== null && items[lightbox] && (
                <div data-testid="gallery-lightbox" className="fixed inset-0 z-[60] bg-black/95 grid place-items-center p-4">
                    <button onClick={() => setLightbox(null)} className="absolute top-5 right-5 text-white/80 hover:text-gold" data-testid="lightbox-close">
                        <X size={32} />
                    </button>
                    <button onClick={() => setLightbox((i) => (i - 1 + items.length) % items.length)} className="absolute left-5 top-1/2 -translate-y-1/2 text-white/80 hover:text-gold">
                        <ChevronLeft size={36} />
                    </button>
                    <button onClick={() => setLightbox((i) => (i + 1) % items.length)} className="absolute right-5 top-1/2 -translate-y-1/2 text-white/80 hover:text-gold">
                        <ChevronRight size={36} />
                    </button>
                    <img
                        src={items[lightbox].image_url}
                        alt={items[lightbox].title}
                        className="max-h-[88vh] max-w-[92vw] object-contain rounded-lg"
                    />
                    <div className="absolute bottom-6 left-0 right-0 text-center text-white/80 text-sm">
                        <span className="text-gold uppercase tracking-widest text-xs mr-3">{items[lightbox].category}</span>
                        {items[lightbox].title}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Gallery;
